import React from 'react';
import { isSafari, isIOS } from 'react-device-detect';
import { Box } from '@mui/material';
import PortraitPaperLayout from '../@common/portrait/PortraitPaperLayout';
import { SmallPageLineText } from '../@common/PageLineText';
import PortraitPageLines from '../@common/portrait/PortraitPageLines';
import BaseInput from '../@common/BaseInput';
import { WorksheetBaseProps } from '../@types';
import NameAndDateLines from '../@common/NameAndDateLines';
import { determineStyleBasedOnLineWidth, getFontSize } from 'utils/style-utils';
import { translate } from '../../translations/translator';

const t = key => translate('worksheet', key);

type WordsAndSpellingProps = WorksheetBaseProps & {
  value: string;
  isCursive?: boolean;
  isNarrow?: boolean;
};

const WordsAndSpelling = ({
  onChange,
  isCursive,
  isNarrow,
  valueMap,
  printRef
}: WordsAndSpellingProps) => {
  const bookFont = isCursive ? 'Cursive-Book' : 'Print-Wide-Book';

  const marginTop = determineStyleBasedOnLineWidth(isCursive, isNarrow, {
    cursiveAndNarrow: isSafari || isIOS ? '-10px' : '-22px',
    narrow: isIOS ? '-16px' : '-17px',
    cursive: isSafari || isIOS ? '-15px' : '-32px',
    regular: '-28px'
  });

  const fontSize = getFontSize(isCursive, isNarrow);
  const rowMargin = isNarrow ? '32px' : '10px 0';
  const rowPadding = isNarrow ? '10px 0 10px 10px' : '40px 0 40px 40px';
  const numOfLines = isNarrow ? 7 : 4;
  const portraitLines = [...Array(numOfLines).keys()];

  const handleChange = (key, value) => {
    onChange({
      [key]: value
    });
  };

  const baseInputStyles: any = {
    fontFamily: bookFont,
    color: '#000',
    style: {
      fontSize: fontSize,
      width: 325,
      marginLeft: 15,
      marginTop: marginTop,
      color: '#000'
    }
  };

  return (
    <PortraitPaperLayout isNarrow={isNarrow} printRef={printRef}>
      <NameAndDateLines
        isCursive={isCursive}
        fontFamily={bookFont}
        isNarrow={isNarrow}
        top={30}
        width={775}
      />
      <Box
        component={'ul'}
        sx={{
          listStyle: 'none',
          padding: 0,
          paddingTop: isNarrow ? '5px' : '45px',
          height: '100%',
          width: '100%'
        }}
        aria-label={t('wordsList')}
      >
        {portraitLines.map(x => (
          <PortraitPageLines
            isNarrow={isNarrow}
            rowMargin={rowMargin}
            rowPadding={rowPadding}
            marginTop={85}
            component={'li'}
            col1={
              <SmallPageLineText isNarrow={isNarrow}>
                <div className="pageNumber">{x + 1}.</div>
                <BaseInput
                  onChange={({ target }) => handleChange(x + 1, target.value)}
                  showBackground={true}
                  {...baseInputStyles}
                  placeholder={t('word')}
                  fontSize={65}
                  value={valueMap[x + 1]}
                  aria-label={`${t('word')} ${x + 1}`}
                />
              </SmallPageLineText>
            }
            col2={
              <SmallPageLineText isNarrow={isNarrow}>
                <div className="pageNumber">{x + numOfLines + 1}.</div>
                <BaseInput
                  onChange={({ target }) => handleChange(x + numOfLines + 1, target.value)}
                  showBackground={true}
                  {...baseInputStyles}
                  placeholder={t('word')}
                  fontSize={65}
                  value={valueMap[x + numOfLines + 1]}
                  aria-label={`${t('word')} ${x + numOfLines + 1}`}
                />
              </SmallPageLineText>
            }
          />
        ))}
      </Box>
    </PortraitPaperLayout>
  );
};

export default WordsAndSpelling;
